import { RoutingInfo } from "#/types/routingInfo"
import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { HelpfulOption } from "#/components/carrot-plans/most-helpful-options/helpfulOption"
import { isNcqaQualifiedJourneyStage, JourneyStage } from "#/components/carrot-plans/types/journeyStage"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { CountryCode } from "#/utils/CountryCode"

export function useGetFilteredHelpfulOptions(
  routingData: RoutingInfo,
  benefit: Benefit,
  options: HelpfulOption[]
): HelpfulOption[] {
  const memberJourneyStage = routingData?.journeyStage
  const { isUsa, company } = useCurrentUser()
  const hideChatAboutCyclesOptionForNoCycles: boolean = memberJourneyStage === JourneyStage.NO_MENSTRUAL_CYCLES
  const isCarrotLite = benefit?.program.isCarrotLite
  const companyHasProviderFinder = benefit?.providerFinder.showProviderFinder
  const hideLGBTQOptions = !benefit?.generalSupport.showLgbtqContent
  const hideFaithInformedCareOptions = !benefit?.generalSupport.faithInformedCareEnabled
  const hideDonorOptions = !benefit?.generalSupport.showDonorContent
  const hideGestationalSurrogacyOptions = !benefit?.journeys.gestationalCarrier.isAppSupportEnabled
  const hideAdoptionOptions = !benefit?.journeys.adoption.isAppSupportEnabled
  const hasLGBTQContentOrGestationalSurrogacy = !hideLGBTQOptions || !hideGestationalSurrogacyOptions
  const hideDoulaProviderOptions = !benefit?.providerFinder.showDoulas
  const companyCountryCodeIsChina = company?.countryCode === CountryCode.China
  const hidePediatricianProviderOption = !isUsa || !benefit?.providerFinder.showPediatricianRecommendations
  const hideObGynProviderOption = !isUsa || !benefit?.providerFinder.showObGynRecommendations
  const hideBlueberryPediatricsOption = !benefit?.telemedicine?.providers?.blueberryPediatricsEnabled
  const hidePelvicFloorTherapyOption = !benefit?.pelvicFloorTherapyTelemedicine?.enabled

  let filteredOptions = options
  if (hideLGBTQOptions) {
    filteredOptions = filteredOptions.filter(
      (opt) =>
        opt !== HelpfulOption.LGBTQ_FAMILY &&
        opt !== HelpfulOption.LGBTQIA_PLUS_SUPPORT &&
        opt !== HelpfulOption.TRANSGENDER_FERTILITY_CONSIDERATIONS &&
        opt !== HelpfulOption.RECIPROCAL_IVF &&
        opt !== HelpfulOption.GENDER_EXPLORATION_EMBODIMENT &&
        opt !== HelpfulOption.FEMINIZING_HORMONE_THERAPY &&
        opt !== HelpfulOption.MASCULINIZING_HORMONE_THERAPY &&
        opt !== HelpfulOption.SOCIAL_AFFIRMATION &&
        opt !== HelpfulOption.MEDICAL_AFFIRMATION &&
        opt !== HelpfulOption.LEGAL_AFFIRMATION &&
        opt !== HelpfulOption.BINDERS_AND_TUCKING_GARMENTS &&
        opt !== HelpfulOption.VOICE_THERAPY &&
        opt !== HelpfulOption.HAIR_REMOVAL &&
        opt !== HelpfulOption.NON_HORMONAL_GENDER_AFFIRMING_CARE_OPTIONS &&
        opt !== HelpfulOption.OTHER_FERTILITY_QUESTIONS
    )
  }
  if (hideFaithInformedCareOptions) {
    filteredOptions = filteredOptions.filter(
      (opt) =>
        opt !== HelpfulOption.FAITH_INCLUSIVE_CARE && opt !== HelpfulOption.FAITH_INCLUSIVE_OPTIONS_FOR_SEMEN_COLLECTION
    )
  }
  if (isCarrotLite) {
    filteredOptions = filteredOptions.filter((opt) => opt !== HelpfulOption.FINANCE_INFO)
  }
  if (hideChatAboutCyclesOptionForNoCycles) {
    filteredOptions = filteredOptions.filter((opt) => opt !== HelpfulOption.CHAT_FERTILITY)
  }
  if (!companyHasProviderFinder && !companyCountryCodeIsChina) {
    filteredOptions = filteredOptions.filter((opt) => opt !== HelpfulOption.HELP_FINDING_PROVIDER)
  }
  if (!isUsa) {
    filteredOptions = filteredOptions.filter((opt) => opt !== HelpfulOption.PRENATAL_YOGA)
  }
  if (hideDonorOptions) {
    filteredOptions = filteredOptions.filter(
      (opt) =>
        opt !== HelpfulOption.SINGLE_INTENDED_PARENT_SUPPORT &&
        opt !== HelpfulOption.DONOR_EGGS &&
        opt !== HelpfulOption.DONOR_SPERM &&
        opt !== HelpfulOption.DONOR_ASSISTANCE
    )
  }
  if (hideAdoptionOptions) {
    filteredOptions = filteredOptions.filter((opt) => opt !== HelpfulOption.ADOPTION)
  }
  if (hideGestationalSurrogacyOptions) {
    filteredOptions = filteredOptions.filter(
      (opt) => opt !== HelpfulOption.GESTATIONAL_SURROGACY_BASICS && opt !== HelpfulOption.GESTATIONAL_SURROGACY
    )
  }
  if (!hasLGBTQContentOrGestationalSurrogacy) {
    filteredOptions = filteredOptions.filter((opt) => opt !== HelpfulOption.INDUCED_LACTATION)
  }
  if (hideDoulaProviderOptions) {
    filteredOptions = filteredOptions.filter((opt) => opt !== HelpfulOption.HELP_FINDING_DOULA)
  }
  if (hidePediatricianProviderOption) {
    filteredOptions = filteredOptions.filter((opt) => opt !== HelpfulOption.HELP_FINDING_PEDIATRICIAN)
  }
  if (hideObGynProviderOption) {
    filteredOptions = filteredOptions.filter((opt) => opt !== HelpfulOption.HELP_FINDING_OBSTETRIC_PROVIDER)
  }
  if (!isNcqaQualifiedJourneyStage(memberJourneyStage)) {
    filteredOptions = filteredOptions.filter((opt) => opt !== HelpfulOption.FINANCIAL_CONCERNS_JOURNEY_COST)
  }
  if (hideBlueberryPediatricsOption) {
    filteredOptions = filteredOptions.filter((opt) => opt !== HelpfulOption.PEDIATRIC_TELEMEDICINE)
  }
  if (hidePelvicFloorTherapyOption) {
    filteredOptions = filteredOptions.filter((opt) => opt !== HelpfulOption.PELVIC_FLOOR_THERAPY)
  }
  return filteredOptions
}
